import React from 'react';
import PropTypes from 'prop-types';
import property from 'lodash/fp/property';

import { Link } from '@nerdwallet/currency';
import Row from '../row/row';
import Paragraph from '../paragraph/paragraph';
import BulletedList from '../bulleted-list/bulleted-list';
import Expandable, {
  omitTrackingData,
  trackingPropTypes,
} from '../expandable/expandable';

import {
  boolCheckboxSpec,
  stringInputSpec,
  repeatable,
  dataDependencyTypePicker,
  dataDependencyNumericIdInput,
  formInputChangeEventFields,
  getEditingFieldsByName,
} from '../../lib/editing-configs';
import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';

const CollapsibleParagraph = ({
  children,
  abstractText,
  abstractBullets,
  showMore,
  showLess,
  reuseAbstract,
  ...otherProps
}) => {
  const paragraphProps = omitTrackingData(otherProps);
  const abstractList = abstractBullets.length ? (
    <BulletedList
      key="abstract-list"
      bullets={abstractBullets.map(property('text'))}
    />
  ) : null;
  const abstract = [
    <Paragraph {...paragraphProps} key="abstract-text" body={abstractText} />,
    abstractList,
  ];

  const expanded = reuseAbstract
    ? abstract.concat(children)
    : [].concat(children);

  return (
    <Expandable
      trackingData={otherProps}
      render={(toggleExpanded, isExpanded) => (
        <Row className="paragraph">
          {isExpanded
            ? expanded.concat(
                <Link
                  key="show-less"
                  onClick={toggleExpanded}
                  className="copy-p2"
                >
                  {showLess}
                </Link>
              )
            : abstract.concat(
                <Link
                  key="show-more"
                  onClick={toggleExpanded}
                  className="copy-p2"
                >
                  {showMore}
                </Link>
              )}
        </Row>
      )}
    />
  );
};

CollapsibleParagraph.propTypes = {
  children: PropTypes.node,
  abstractText: PropTypes.node,
  abstractBullets: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
    })
  ),
  reuseAbstract: PropTypes.bool,
  showMore: PropTypes.string,
  showLess: PropTypes.string,

  ...trackingPropTypes,
};

CollapsibleParagraph.defaultProps = {
  children: [],
  abstractBullets: [],
  reuseAbstract: true,
  showMore: 'Show more',
  showLess: 'Show less',
};

const getParagraphField = getEditingFieldsByName(Paragraph);

const abstractTextConfig = {
  ...getParagraphField('body'),
  fieldName: 'abstractText',
  fieldLabel: 'Abstract text',
};

CollapsibleParagraph.getEditingConfig = () => [
  getParagraphField('preheader'),
  getParagraphField('header'),
  getParagraphField('headingStyle'),
  boolCheckboxSpec('reuseAbstract', 'Abstract', {
    items: [{ label: 'Visible when module is expanded', value: 'true' }],
    help: 'Uncheck to hide abstract when the module is expanded.',
  }),
  abstractTextConfig,
  repeatable('abstractBullets', 'Abstract bulleted list', 'bullet', () => [
    stringInputSpec('text', 'Bullet'),
  ]),
  stringInputSpec('showMore', '"Show more" text'),
  stringInputSpec('showLess', '"Show less" text'),
  ...formInputChangeEventFields,
  dataDependencyTypePicker,
  dataDependencyNumericIdInput,
];

CollapsibleParagraph.isContainer = true;

export default withOutOfDateMessaging(CollapsibleParagraph);
